import React            from 'react';
import { observer }     from 'mobx-react-lite';
import debounce         from 'lodash/debounce';
import View             from '@widesk-ui/components/View';
import Title            from '@widesk-ui/components/Title';
import Spin             from '@widesk-ui/components/Spin';
import { InputSearch }  from '@widesk-ui/components/form/Input';
import { CheckOutlined } from '@ant-design/icons';
import useDesignTokens  from '@/hooks/useDesignTokens';

const MENU_WIDTH = 320;
const LIST_MAX_HEIGHT = 320;
const SEARCH_DEBOUNCE = 300;

/**
 * Coquille commune aux menus « changer de… » de l'éditeur : celui des cartes (`MapMenu`) et celui
 * des projets (`ProjectMenu`), qui sont le même objet à un cran d'écart dans la hiérarchie. Elle
 * porte l'ossature — largeur, en-tête compté, recherche, liste défilante — et laisse à chaque menu
 * ses lignes, qui n'affichent pas les mêmes choses.
 *
 * La recherche est ici seulement saisie : le filtrage reste chez l'appelant, les deux services ne
 * cherchant pas dans le même champ (`label` pour les cartes, `title` pour les projets) et aucun des
 * deux n'étant filtrable côté API.
 */
export const SwitcherMenu = observer((props: {
	title: string;
	count?: number;
	searchPlaceholder: string;
	onSearch: (value?: string) => void;
	extra?: React.ReactNode; // action posée sous la recherche (bouton d'ajout…)
	loading?: boolean;
	children: React.ReactNode; // les lignes, et l'état vide quand il n'y en a pas
}) => {
	const tokens = useDesignTokens();

	const { onSearch } = props;

	const debouncedSearch = React.useMemo(() => debounce(onSearch, SEARCH_DEBOUNCE), [onSearch]);

	return (
		<View style={{ width: MENU_WIDTH }} p="sm" gap="xs">
			<View row center gap="xs" style={{ justifyContent: 'flex-start' }}>
				<Title level={4} marginB={0} bold align="left">{props.title}</Title>

				{typeof props.count !== 'undefined' && (
					<View
						px="xs"
						br={20}
						bg={tokens.fill.subtle}
						style={{ fontSize: 11, fontWeight: 700, color: tokens.color.primary }}
					>
						{props.count}
					</View>
				)}
			</View>

			<InputSearch
				placeholder={props.searchPlaceholder}
				allowClear
				onChange={e => debouncedSearch(e.target.value || undefined)}
				onSearch={value => onSearch(value || undefined)}
			/>

			{props.extra}

			<View height={1} bg={tokens.border.default} marginV="xs" />

			{props.loading ? (
				<View p="sm" center>
					<Spin size="small" />
				</View>
			) : (
				<View gap={2} maxHeight={LIST_MAX_HEIGHT} overflow="auto">
					{props.children}
				</View>
			)}
		</View>
	);
});

/**
 * Ligne de ces menus : le liseré de couleur qui marque l'élément courant, le fond actif et le
 * survol. Le contenu — ce qui distingue une carte d'un projet — est laissé à l'appelant.
 *
 * `disabled` garde la ligne visible mais inerte. C'est à l'appelant de dire pourquoi, dans le
 * contenu de la ligne : `View` ne relaie pas `title` au DOM, pas d'infobulle native ici.
 */
export const SwitcherMenuRow = observer((props: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
	innerRef?: React.Ref<HTMLDivElement>;
	onHover?: (hovered: boolean) => void;
	disabled?: boolean;
}) => {
	const tokens = useDesignTokens();

	return (
		<View
			ref={props.innerRef}
			row
			gap="sm"
			p="xs"
			br={8}
			onHover={props.disabled ? undefined : props.onHover}
			hover={props.disabled ? undefined : { bg: tokens.surface.subtle }}
			bg={props.active ? tokens.fill.subtle : undefined}
			onClick={props.disabled ? undefined : props.onClick}
			rel
			style={{
				alignItems: 'stretch',
				// `opacity` plutôt qu'une couleur de texte : elle atteint aussi la vignette et les pastilles.
				...(props.disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined),
			}}
		>
			<View width={4} br={3} bg={props.active ? tokens.color.primary : 'transparent'} />

			{props.children}
		</View>
	);
});

/** Pastille ✓ de l'élément courant, posée devant son titre. */
export const SwitcherMenuCheck = () => {
	const tokens = useDesignTokens();

	return (
		<View
			center
			style={{
				width: 16,
				height: 16,
				flex: '0 0 auto',
				borderRadius: '50%',
				background: tokens.color.primary,
			}}
		>
			<CheckOutlined style={{ color: '#fff', fontSize: 10 }} />
		</View>
	);
};
